import { sendMessage } from "./iframeBridge";
import { ExtensionAction, MessageType, ExtensionEventType } from "../types";

import type {
  AdBlockerType,
  ExtensionStatusResponse,
  AllowlistResponse,
  IframeResponse,
} from "../types";

/**
 * Module that manages extension API detection and communication
 *
 * This module handles:
 * - Detection of browser extensions (ad blockers)
 * - Communication with extensions via message passing
 * - Allowlisting websites in compatible extensions
 * - Retrieving extension status information
 */

let isInitialized = false;
let initializationPromise: Promise<void> | null = null;
const API_STATUS_TIMEOUT = 1000;
const RETRY_DELAY = 50;

/**
 * Gets the API availability state from window object for persistence across code executions
 *
 * @returns {boolean|null} Current API availability state or null if not yet determined
 */
function getIsApiAvailable(): boolean | null {
  return (
    (window as unknown as { __bt_rlink_extension_api: boolean | null })
      .__bt_rlink_extension_api ?? null
  );
}

/**
 * Sets the API availability state on window object for persistence
 *
 * @param {boolean|null} value - API availability state
 */
function setIsApiAvailable(value: boolean | null): void {
  (
    window as unknown as { __bt_rlink_extension_api: boolean | null }
  ).__bt_rlink_extension_api = value;
}

/**
 * Gets extension information from window object for persistence across code executions
 *
 * @returns {ExtensionStatusResponse[]|null} Current extension information or null if not yet detected
 */
function getExtensionInfo(): ExtensionStatusResponse[] | null {
  return (
    (
      window as unknown as {
        __bt_rlink_extension_info: ExtensionStatusResponse[] | null;
      }
    ).__bt_rlink_extension_info ?? null
  );
}

/**
 * Sets extension information on window object for persistence
 *
 * @param {ExtensionStatusResponse[]|null} value - Extension information to store
 */
function setExtensionInfo(value: ExtensionStatusResponse[] | null): void {
  (
    window as unknown as {
      __bt_rlink_extension_info: ExtensionStatusResponse[] | null;
    }
  ).__bt_rlink_extension_info = value;
}

/**
 * Waits for API status response from extensions with a timeout
 *
 * @returns {Promise<boolean>} Promise resolving to whether the API is available
 */
async function waitForApiStatus(): Promise<boolean> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      if (getIsApiAvailable() === null) {
        window.removeEventListener("message", messageHandler);
        setIsApiAvailable(false);
        resolve(false);
      }
    }, API_STATUS_TIMEOUT);

    const messageHandler = (event: MessageEvent) => {
      if (event.data.type === ExtensionEventType.ApiStatus) {
        clearTimeout(timeout);
        setIsApiAvailable(event.data.available);
        resolve(event.data.available || false);
        window.removeEventListener("message", messageHandler);
      }
    };

    window.addEventListener("message", messageHandler);
  });
}

/**
 * Updates AA detection results based on extension information
 *
 * When extension API provides AA information, we can skip the manual
 * detection process and use this data directly.
 */
function updateAADetectionFromExtensionInfo(): void {
  const extensionInfo = getExtensionInfo();
  if (!extensionInfo) return;

  const info = extensionInfo.find(
    (resp) => resp.name === "adblockplus" || resp.name === "adblock",
  );

  if (info?.result) {
    if (!window.__bt_intrnl) {
      // @ts-expect-error: Initializing __bt_intrnl properly
      window.__bt_intrnl = {};
    }

    window.__bt_intrnl.aaDetectionResults = {
      ab: true,
      acceptable: info.result.aa,
    };
  }
}

/**
 * Validates if a message origin is from a trusted source
 * @param origin - The origin to validate
 * @returns True if the origin is valid
 */
function isValidOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" ||
      (url.hostname === "localhost" && url.port === "3002")
    );
  } catch {
    return false;
  }
}

/**
 * Setup event listeners for extension-related events
 */
function setupExtensionDetection(): void {
  if (isInitialized) return;

  window.addEventListener("message", (event) => {
    if (!isValidOrigin(event.origin)) {
      return;
    }

    const data = event.data;

    if (data?.type === ExtensionEventType.ApiStatus) {
      setIsApiAvailable(data.available);
    }

    if (
      data?.type === ExtensionEventType.Detected &&
      data.value &&
      !getExtensionInfo()
    ) {
      setExtensionInfo(data.value as ExtensionStatusResponse[]);
      updateAADetectionFromExtensionInfo();
    }
  });

  isInitialized = true;
}

/**
 * Initializes the extension API detection system by setting up event listeners.
 *
 * @returns {Promise<void>} A promise that resolves when initialization is complete
 * @description This function sets up event listeners and prepares the API for use.
 * It should be called early in the application lifecycle before using other extension API functions.
 * The function is idempotent - calling it multiple times will only initialize once.
 */
export async function initializeRLinkIframe(): Promise<void> {
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    setupExtensionDetection();
  })();

  return initializationPromise;
}

/**
 * Checks if the extension API is available in the current environment
 *
 * @returns {Promise<boolean>} Promise resolving to whether the API is available
 */
export async function checkExtensionApiAvailable(): Promise<boolean> {
  if (!isInitialized) {
    await initializeRLinkIframe();
  }

  const currentAvailability = getIsApiAvailable();
  if (currentAvailability === null) {
    return waitForApiStatus();
  }

  return currentAvailability;
}

/**
 * Detects installed extensions and their configuration
 *
 * @returns {Promise<ExtensionStatusResponse[] | null>} Promise resolving to extension
 * information or null if none detected
 */
export async function detectExtension(): Promise<
  ExtensionStatusResponse[] | null
> {
  if (!isInitialized) {
    await initializeRLinkIframe();
  }

  const currentInfo = getExtensionInfo();
  if (currentInfo) {
    return currentInfo;
  }

  let retries = 0;
  const maxRetries = 3;

  while (retries < maxRetries) {
    try {
      const response = await sendMessage<
        IframeResponse<ExtensionStatusResponse[]>
      >({
        action: ExtensionAction.GetStatus,
        trustedRLEvent: true,
        messageType: MessageType.Extension,
      });

      if (!getExtensionInfo() && response) {
        setExtensionInfo(response.value);
        updateAADetectionFromExtensionInfo();
      }

      return getExtensionInfo();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (errorMessage.includes("pending") && retries < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
        continue;
      }

      return null;
    }
  }

  return null;
}

/**
 * Gets information about the detected extension, including type and configuration
 *
 * @returns {Promise<{detectedExtension: AdBlockerType, extensionInfo: ExtensionStatusResponse | null}>}
 * Promise resolving to an object containing the detected extension type and its information
 */
export async function getDetectedExtension(): Promise<{
  detectedExtension: AdBlockerType;
  extensionInfo: ExtensionStatusResponse | null;
}> {
  const responses = await detectExtension();

  if (!responses) {
    return { detectedExtension: "unknown", extensionInfo: null };
  }

  const extensionInfo = responses.find(
    (resp) => resp.name === "adblockplus" || resp.name === "adblock",
  );

  if (!extensionInfo) {
    return { detectedExtension: "unknown", extensionInfo: null };
  }

  const adblockers: Record<string, AdBlockerType> = {
    adblockplus: "abp",
    adblock: "ab",
  };

  return {
    detectedExtension: adblockers[extensionInfo.name] || "unknown",
    extensionInfo,
  };
}

/**
 * Sends an allowlist request to all detected ad blocker extensions
 *
 * @param {number} expiresAt - Timestamp when the allowlist should expire
 * @param {{ signedPayload: string }} signedPayloadData - Signed payload data for verification
 * @returns {Promise<boolean>} Promise resolving to whether allowlisting was successful with any extension
 * @description The response is handled as an array because:
 * 1. Users may have multiple ad blocker extensions installed simultaneously
 * 2. Each extension responds individually with its own result
 * 3. We consider the operation successful if ANY extension processes it successfully
 */
export async function allowlistWebsite(
  expiresAt: number,
  signedPayloadData: { signedPayload: string },
): Promise<boolean> {
  if (!isInitialized) {
    await initializeRLinkIframe();
  }

  try {
    const response = await sendMessage<AllowlistResponse[]>({
      action: ExtensionAction.Allowlist,
      payload: {
        expiresAt,
        signedPayload: signedPayloadData.signedPayload,
      },
      trustedRLEvent: true,
      messageType: MessageType.Extension,
    });

    if (!response) {
      return false;
    }

    return Array.isArray(response)
      ? response.some((r) => r.result?.status === true)
      : false;
  } catch {
    return false;
  }
}
